import React, { useState, useEffect } from "react";
import {
  useQuery,
  useMutation,
  useQuery as useApolloQuery,
} from "@apollo/client";
import {
  VEHICLE_CREATE,
  VEHICLE_UPDATE,
  VEHICLE_GET_LIST_FILTER,
  VEHICLE_WARRANTY_CREATE,
} from "../../graphql/vehicles";
import { GUARDS_LIST } from "../../graphql/guards";
import { INSURANCE_POLICIES_LIST } from "../../graphql/insurancePolicies";
import { BUSINESS_UNIT_LIST } from "../../graphql/employees";
import { ASSIGNMENT_CREATE } from "../../graphql/employeeVehicleAssignments";
import { REGISTRATION_CREATE } from "../../graphql/registrations";
import {
  TextField,
  Button,
  MenuItem,
  Box,
  CircularProgress,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AddIcon from "@mui/icons-material/Add";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import SpeedIcon from "@mui/icons-material/Speed";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from "@mui/icons-material/Business";
import SecurityIcon from "@mui/icons-material/Security";
import EditableFormField from "../../components/editable-form-field";
import { useNavigate } from "react-router-dom";
import DatePickerAuto from "../../components/custom-datepicker/DatePickerAuto";
import MaintenanceForm from "./MaintenanceForm";
import WorkOrderForm from "./WorkOrderForm";
import InspectionForm from "./InspectionForm";

// Modal Form Components

const AssignmentModalForm = ({
  vehicleId,
  vehicleName,
  onClose,
  onSuccess,
}) => {
  const [form, setForm] = useState({
    employeeId: "",
    assignedDate: "",
    unassignedDate: "",
    role: "driver",
  });

  const { data: employeesData } = useQuery(GUARDS_LIST);
  const [createAssignment, { loading: creatingAssignment }] = useMutation(ASSIGNMENT_CREATE, {
    refetchQueries: ['VehicleGet'],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createAssignment({
        variables: {
          data: {
            assignedDate: form.assignedDate,
            unassignedDate: form.unassignedDate || null,
            role: form.role,
            employee: { connect: { id: form.employeeId } },
            vehicle: { connect: { id: vehicleId } },
          },
        },
      });
      onSuccess();
    } catch (err) {
      console.error("Error creating assignment:", err);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Vehicle: {vehicleName}
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Employee</InputLabel>
        <Select
          name="employeeId"
          value={form.employeeId}
          onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
          label="Employee"
        >
          {employeesData?.guardsList?.items?.map((emp) => (
            <MenuItem key={emp.id} value={emp.id}>
              {emp.firstName} {emp.lastName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <DatePickerAuto
        editTab={true}
        sx={{ mb: 2 }}
        rest={{}}
        value={form.assignedDate}
        label="Assigned Date"
        onUpdate={(date) => {
          setForm(prev => ({ ...prev, assignedDate: date }));
        }}
      />

      <DatePickerAuto
        editTab={true}
        rest={{}}
        value={form.unassignedDate}
        label="Unassigned Date (Optional)"
        onUpdate={(date) => {
          setForm(prev => ({ ...prev, unassignedDate: date }));
        }}
      />

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={creatingAssignment}>
          {creatingAssignment ? <CircularProgress size={24} /> : "Create"}
        </Button>
      </DialogActions>
    </Box>
  );
};



const WarrantyModalForm = ({ vehicleId, vehicleName, onClose, onSuccess }) => {
  // Get today's date in yyyy-mm-dd format
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    component: "",
    scheduledDate: "",
    startDate: today, // Autofill with today
    endDate: "",
    company_name: "",
    warranty_details: "",
    warranty_type: "",
  });

  const [createWarranty, { loading: creatingWarranty }] = useMutation(VEHICLE_WARRANTY_CREATE, {
    refetchQueries: ['VehicleGet'],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createWarranty({
        variables: {
          data: {
            company_name: form.company_name,
            endDate: form.endDate,
            startDate: form.startDate,
            warranty_details: form.warranty_details,
            warranty_type: form.warranty_type,
            warrantyRecords: {
              connect: { id: vehicleId },
            },
          },
        },
      });
      onSuccess();
    } catch (err) {
      // Optionally handle error
      console.error("Error creating warranty record:", err);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Vehicle: {vehicleName}
      </Typography>

      {/* Warranty Type Dropdown */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="warranty-type-label">Warranty Type</InputLabel>
        <Select
          labelId="warranty-type-label"
          id="warranty-type"
          name="warranty_type"
          value={form.warranty_type}
          label="Warranty Type"
          onChange={(e) => setForm({ ...form, warranty_type: e.target.value })}
        >
          <MenuItem value="manufacturer">Manufacturer</MenuItem>
          <MenuItem value="third party">Third Party</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Company Name"
        name="company_name"
        value={form.company_name}
        onChange={(e) => setForm({ ...form, company_name: e.target.value })}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Warranty Details"
        name="warranty_details"
        value={form.warranty_details}
        onChange={(e) => setForm({ ...form, warranty_details: e.target.value })}
        sx={{ mb: 2 }}
      />

      {/* <TextField
        fullWidth
        label="Scheduled Date"
        type="date"
        name="scheduledDate"
        value={form.scheduledDate}
        onChange={(e) => setForm({ ...form, scheduledDate: e.target.value })}
        sx={{ mb: 2 }}
        InputLabelProps={{ shrink: true }}
      /> */}

      <DatePickerAuto
        editTab={true}
        rest={{}}
        value={form.startDate}
        label="Start Date"
        onUpdate={(date) => setForm({ ...form, startDate: date })}
      />

      <DatePickerAuto
        editTab={true}
        rest={{}}
        value={form.endDate}
        label="End Date"
        onUpdate={(date) => setForm({ ...form, endDate: date })}
      />

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={creatingWarranty}>
          {creatingWarranty ? <CircularProgress size={24} /> : "Create"}
        </Button>
      </DialogActions>
    </Box>
  );
};

const RegistrationModalForm = ({
  vehicleId,
  vehicleName,
  onClose,
  onSuccess,
}) => {
  const [form, setForm] = useState({
    registrationNumber: "",
    annualDate: "",
    expiryDate: "",
  });

  const [createRegistration, { loading: creatingRegistration }] = useMutation(REGISTRATION_CREATE, {
    refetchQueries: ['VehicleGet'],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createRegistration({
        variables: {
          data: {
            registrationNumber: form.registrationNumber,
            annualDate: form.annualDate,
            expiryDate: form.expiryDate,
            registrations: { connect: { id: vehicleId } },
          },
        },
      });
      onSuccess();
    } catch (err) {
      console.error("Error creating registration:", err);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Vehicle: {vehicleName}
      </Typography>

      <TextField
        fullWidth
        label="Registration Number"
        name="registrationNumber"
        value={form.registrationNumber}
        onChange={(e) =>
          setForm({ ...form, registrationNumber: e.target.value })
        }
        sx={{ mb: 2 }}
      />

      <DatePickerAuto
        editTab={true}
        rest={{}}
        value={form.annualDate}
        label="Annual Date"
        onUpdate={(date) => setForm({ ...form, annualDate: date })}
      />

      <DatePickerAuto
        editTab={true}
        rest={{}}
        value={form.expiryDate}
        label="Expiry Date"
        onUpdate={(date) => setForm({ ...form, expiryDate: date })}
      />

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={creatingRegistration}>
          {creatingRegistration ? <CircularProgress size={24} /> : "Create"}
        </Button>
      </DialogActions>
    </Box>
  );
};

const statusOptions = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
  { value: "Retired", label: "Retired" },
  { value: "Maintenance", label: "Maintenance" },
];

const VehicleForm = ({
  vehicle,
  onClose,
  isEditing: externalIsEditing,
  onToggleEdit,
}) => {
  const navigate = useNavigate();
  const [internalIsEditing, setInternalIsEditing] = useState(!vehicle); // Start in edit mode for new vehicles, view mode for existing
  const isEditing =
    externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = onToggleEdit || setInternalIsEditing;

  // Modal states
  const [maintenanceModal, setMaintenanceModal] = useState(false);
  const [assignmentModal, setAssignmentModal] = useState(false);
  const [workOrderModal, setWorkOrderModal] = useState(false);
  const [inspectionModal, setInspectionModal] = useState(false);
  const [warrantyModal, setWarrantyModal] = useState(false);
  const [registrationModal, setRegistrationModal] = useState(false);

  const [form, setForm] = useState({
    unitNumber: "",
    make: "",
    model: "",
    year: "",
    vIN: "",
    purchaseDate: "",
    status: "Active",
    licensePlate: "",
    insurancePolicy: "",
    businessUnit: "",
  });

  // Modal opening functions
  const handleAddMaintenance = () => {
    setMaintenanceModal(true);
  };

  const handleAddAssignment = () => {
    setAssignmentModal(true);
  };

  const handleAddWorkOrder = () => {
    setWorkOrderModal(true);
  };

  const handleAddInspection = () => {
    setInspectionModal(true);
  };

  const handleAddWarranty = () => {
    setWarrantyModal(true);
  };

  const handleAddRegistration = () => {
    setRegistrationModal(true);
  };

  const { data: driversData, loading: driversLoading } = useQuery(GUARDS_LIST);
  const { data: insuranceData, loading: insuranceLoading } = useApolloQuery(
    INSURANCE_POLICIES_LIST
  );
  const { data: businessUnitsData, loading: businessUnitsLoading } = useQuery(BUSINESS_UNIT_LIST);
  const { data: vehicleData, loading: vehicleLoading, refetch: refetchVehicle } = useQuery(
    VEHICLE_GET_LIST_FILTER,
    { variables: { id: vehicle?.id }, skip: !vehicle?.id }
  );
  const [createVehicle, { loading: creating }] = useMutation(VEHICLE_CREATE, {
    refetchQueries: ["VehiclesList", "VehiclesListFiltered"],
  });
  const [updateVehicle, { loading: updating }] = useMutation(VEHICLE_UPDATE, {
    refetchQueries: ["VehiclesList", "VehiclesListFiltered"],
  });

  const [createWarranty, { loading: creatingWarranty }] = useMutation(VEHICLE_WARRANTY_CREATE, {
    refetchQueries: ["VehiclesList", "VehiclesListFiltered"],
  });

  useEffect(() => {
    if (vehicle?.id && vehicleData?.vehiclesList?.items?.length) {
      const v = vehicleData.vehiclesList.items[0];
      setForm({
        unitNumber: v.unitNumber || "",
        make: v.make || "",
        model: v.model || "",
        year: v.year || "",
        vIN: v.vIN || "",
        purchaseDate: v.purchaseDate ? v.purchaseDate.substring(0, 10) : "",
        status: v.status || "Active",
        licensePlate: v.licensePlate || "",
        insurancePolicy: v.insurancePolicy?.id || "",
        businessUnit: v.businessUnit?.id || "",
      });
    } else if (!vehicle) {
      setForm({
        unitNumber: "",
        make: "",
        model: "",
        year: "",
        vIN: "",
        purchaseDate: "",
        status: "Active",
        licensePlate: "",
        insurancePolicy: "",
        businessUnit: "",
      });
    }
  }, [vehicle, vehicleData]);

  if (vehicle?.id && vehicleLoading) return <CircularProgress />;

  // Get the current vehicle data from the refetched query
  const currentVehicle = vehicleData?.vehiclesList?.items?.[0] || vehicle;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDriversChange = (event) => {
    const value = event.target.value;
    setForm({
      ...form,
      permittedDrivers: typeof value === "string" ? value.split(",") : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...form,
      year: parseInt(form.year, 10),
      purchaseDate: form.purchaseDate ? form.purchaseDate : null,
      insurancePolicy: form.insurancePolicy
        ? { connect: { id: form.insurancePolicy } }
        : undefined,
      businessUnit: form.businessUnit
        ? { connect: { id: form.businessUnit } }
        : undefined,
    };
    if (vehicle) {
      await updateVehicle({ variables: { filter: { id: vehicle.id }, data } });
    } else {
      await createVehicle({ variables: { data } });
    }
    onClose(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (vehicle) {
      // Reset form to original values
      const v = vehicleData.vehiclesList.items[0];
      setForm({
        unitNumber: v.unitNumber || "",
        make: v.make || "",
        model: v.model || "",
        year: v.year || "",
        vIN: v.vIN || "",
        purchaseDate: v.purchaseDate ? v.purchaseDate.substring(0, 10) : "",
        status: v.status || "Active",
        licensePlate: v.licensePlate || "",
        insurancePolicy: v.insurancePolicy?.id || "",
      });
      setIsEditing(false);
    } else {
      onClose();
    }
  };

  const insuranceOptions =
    insuranceData?.insurancePoliciesList?.items?.map((policy) => ({
      value: policy.id,
      label: `${policy.policyNumber} - ${policy.provider}`,
    })) || [];

  const businessUnitOptions =
    businessUnitsData?.businessUnitsList?.items?.map((bu) => ({
      value: bu.id,
      label: bu.name,
    })) || [];

  // Add navigation handler for work orders
  const handleWorkOrderClick = (workOrderId) => {
    navigate(`/fleet/work-orders/${workOrderId}`);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>

      {/* Edit Button for View Mode - Top */}
      {vehicle && !isEditing && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            onClick={handleEdit}
            variant="contained"
            size="large"
            sx={{ 
              minWidth: 120,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
              }
            }}
          >
            Edit
          </Button>
        </Box>
      )}

      {/* Vehicle Header Card */}
      {vehicle && (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                <DirectionsCarIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {vehicle.make} {vehicle.model}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Unit #{vehicle.unitNumber} • {vehicle.licensePlate}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip 
                    label={vehicle.status} 
                    size="small" 
                    sx={{ 
                      bgcolor: vehicle.status === 'Active' ? '#4caf50' : 
                              vehicle.status === 'Maintenance' ? '#ff9800' : '#f44336',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                  <Chip 
                    label={`${vehicle.year}`} 
                    size="small" 
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Main Form Grid */}
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={12}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: '#667eea', width: 40, height: 40, mr: 2 }}>
                  <DirectionsCarIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Basic Information
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <EditableFormField
                    type="select"
                    label="Business Unit"
                    name="businessUnit"
                    value={form.businessUnit}
                    onChange={handleChange}
                    isEditing={isEditing}
                    options={businessUnitOptions}
                    disabled={businessUnitsLoading}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <EditableFormField
                    type="text"
                    label="Unit Number"
                    name="unitNumber"
                    value={form.unitNumber}
                    onChange={handleChange}
                    isEditing={isEditing}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <EditableFormField
                    type="text"
                    label="Make"
                    name="make"
                    value={form.make}
                    onChange={handleChange}
                    isEditing={isEditing}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <EditableFormField
                    type="text"
                    label="Model"
                    name="model"
                    value={form.model}
                    onChange={handleChange}
                    isEditing={isEditing}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <EditableFormField
                    type="number"
                    label="Year"
                    name="year"
                    value={form.year}
                    onChange={handleChange}
                    isEditing={isEditing}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <EditableFormField
                    type="text"
                    label="License Plate"
                    name="licensePlate"
                    value={form.licensePlate}
                    onChange={handleChange}
                    isEditing={isEditing}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <EditableFormField
                    type="text"
                    label="VIN"
                    name="vIN"
                    value={form.vIN}
                    onChange={handleChange}
                    isEditing={isEditing}
                    required
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Details */}
        <Grid item xs={12} md={12}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: '#10b981', width: 40, height: 40, mr: 2 }}>
                  <BusinessIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Additional Details
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <EditableFormField
                    type="select"
                    label="Status"
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    isEditing={isEditing}
                    options={statusOptions}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePickerAuto
                    editTab={isEditing}
                    rest={{}}
                    value={form.purchaseDate}
                    label="Purchase Date"
                    sx={{width: '100%', mt: "16px", mb: "8px"}}
                    onUpdate={(date) => {
                      setForm(prev => ({ ...prev, purchaseDate: date }));
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <EditableFormField
                    type="select"
                    label="Insurance Policy"
                    name="insurancePolicy"
                    value={form.insurancePolicy}
                    onChange={handleChange}
                    isEditing={isEditing}
                    options={insuranceOptions}
                    disabled={insuranceLoading}
                  />
                </Grid>

              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section Divider */}
      <Box sx={{ 
        my: 4, 
        borderBottom: '2px solid #e2e8f0',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-8px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '16px',
          height: '16px',
          backgroundColor: '#667eea',
          borderRadius: '50%',
          border: '3px solid #ffffff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }
      }} />

      {vehicle && (
        <>
          {/* Records Section */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
              Vehicle Records
            </Typography>
            
            <Grid container spacing={3}>
              {/* Maintenance Records */}
              <Grid item xs={12}>
                <Card sx={{ 
                  height: '100%', 
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ bgcolor: '#ff9800', width: 40, height: 40, mr: 2 }}>
                          <BuildIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Maintenance Records
                        </Typography>
                      </Box>
                      <Tooltip title="Add Maintenance Record">
                        <IconButton
                          onClick={handleAddMaintenance}
                          sx={{ 
                            bgcolor: '#ff9800', 
                            color: 'white',
                            '&:hover': { bgcolor: '#f57c00' }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    {currentVehicle.maintenanceRecords?.items?.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ background: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Performed</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Cost</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentVehicle.maintenanceRecords.items.map((rec, idx) => (
                              <TableRow key={rec.id} sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}>
                                <TableCell>{rec.performedDate}</TableCell>
                                <TableCell>{rec.description}</TableCell>
                                <TableCell>${rec.cost}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <BuildIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          No maintenance records found
                        </Typography>
                      </Box>
                                         )}
                   </CardContent>
                 </Card>
               </Grid>

               {/* Section Divider */}
               <Grid item xs={12}>
                 <Box sx={{ 
                   my: 3, 
                   borderBottom: '1px solid #e2e8f0',
                   position: 'relative',
                   '&::after': {
                     content: '""',
                     position: 'absolute',
                     bottom: '-6px',
                     left: '50%',
                     transform: 'translateX(-50%)',
                     width: '12px',
                     height: '12px',
                     backgroundColor: '#2196f3',
                     borderRadius: '50%',
                     border: '2px solid #ffffff',
                     boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                   }
                 }} />
               </Grid>

               {/* Work Orders */}
                <Grid item xs={12} >
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ bgcolor: '#2196f3', width: 40, height: 40, mr: 2 }}>
                            <ListAltIcon />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Work Orders
                          </Typography>
                        </Box>
                        <Tooltip title="Add Work Order">
                          <IconButton
                            onClick={handleAddWorkOrder}
                            sx={{ 
                              bgcolor: '#2196f3', 
                              color: 'white',
                              '&:hover': { bgcolor: '#1976d2' }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    
                    {currentVehicle.workOrders?.items && currentVehicle.workOrders.items.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ background: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Completion</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentVehicle.workOrders.items.map((wo, idx) => (
                              <TableRow 
                                key={wo.id} 
                                sx={{ 
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: '#e3f2fd' }
                                }}
                                onClick={() => handleWorkOrderClick(wo.id)}
                              >
                                <TableCell>{wo.title || 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={wo.status} 
                                    size="small" 
                                    color={wo.status === 'Completed' ? 'success' : 
                                           wo.status === 'In Progress' ? 'warning' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>{wo.estimatedCompletionDate || 'N/A'}</TableCell>
                                <TableCell>{wo.assignedTo?.firstName} {wo.assignedTo?.lastName}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <ListAltIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          No work orders found
                        </Typography>
                      </Box>
                    )}
                   </CardContent>
                 </Card>
               </Grid>

               {/* Section Divider */}
               <Grid item xs={12}>
                 <Box sx={{ 
                   my: 3, 
                   borderBottom: '1px solid #e2e8f0',
                   position: 'relative',
                   '&::after': {
                     content: '""',
                     position: 'absolute',
                     bottom: '-6px',
                     left: '50%',
                     transform: 'translateX(-50%)',
                     width: '12px',
                     height: '12px',
                     backgroundColor: '#9c27b0',
                     borderRadius: '50%',
                     border: '2px solid #ffffff',
                     boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                   }
                 }} />
               </Grid>

               {/* Assignments */}
                <Grid item xs={12} >
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ bgcolor: '#9c27b0', width: 40, height: 40, mr: 2 }}>
                            <AssignmentIndIcon />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Assignments
                          </Typography>
                        </Box>
                        <Tooltip title="Add Assignment">
                          <IconButton
                            onClick={handleAddAssignment}
                            sx={{ 
                              bgcolor: '#9c27b0', 
                              color: 'white',
                              '&:hover': { bgcolor: '#7b1fa2' }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    
                    {currentVehicle.assignments?.items?.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ background: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Start</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>End</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentVehicle.assignments.items.map((a, idx) => (
                              <TableRow key={a.id} sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}>
                                <TableCell>{a.startDate}</TableCell>
                                <TableCell>{a.endDate}</TableCell>
                                <TableCell>
                                  {a.employee ? `${a.employee.firstName} ${a.employee.lastName}` : "-"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <AssignmentIndIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          No assignments found
                        </Typography>
                      </Box>
                                         )}
                   </CardContent>
                 </Card>
               </Grid>

               {/* Section Divider */}
               <Grid item xs={12}>
                 <Box sx={{ 
                   my: 3, 
                   borderBottom: '1px solid #e2e8f0',
                   position: 'relative',
                   '&::after': {
                     content: '""',
                     position: 'absolute',
                     bottom: '-6px',
                     left: '50%',
                     transform: 'translateX(-50%)',
                     width: '12px',
                     height: '12px',
                     backgroundColor: '#4caf50',
                     borderRadius: '50%',
                     border: '2px solid #ffffff',
                     boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                   }
                 }} />
               </Grid>

               {/* Inspections */}
                <Grid item xs={12} >
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ bgcolor: '#4caf50', width: 40, height: 40, mr: 2 }}>
                            <FactCheckIcon />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Inspections
                          </Typography>
                        </Box>
                        <Tooltip title="Add Inspection">
                          <IconButton
                            onClick={handleAddInspection}
                            sx={{ 
                              bgcolor: '#4caf50', 
                              color: 'white',
                              '&:hover': { bgcolor: '#388e3c' }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    
                    {currentVehicle.inspections?.items && currentVehicle.inspections.items.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ background: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Vehicle</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Inspector</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Checklist</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Work Orders</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentVehicle.inspections.items.map((insp, idx) => (
                              <TableRow key={insp.id} sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}>
                                {/* Title */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ bgcolor: '#ef4444', width: 24, height: 24, fontSize: '0.75rem' }}>
                                      T
                                    </Avatar>
                                    <Typography variant="body2">
                                      {insp.title || 'No title'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                
                                {/* Category */}
                                <TableCell>
                                  <Chip 
                                    label={insp.category || 'Not Specified'} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ 
                                      bgcolor: '#f5f5f5', 
                                      borderColor: '#d1d5db',
                                      color: '#6b7280'
                                    }}
                                  />
                                </TableCell>
                                
                                {/* Vehicle */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ bgcolor: '#8b5cf6', width: 24, height: 24 }}>
                                      <DirectionsCarIcon sx={{ fontSize: 14 }} />
                                    </Avatar>
                                    <Typography variant="body2">
                                      {vehicle?.make} {vehicle?.model}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                
                                {/* Date */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ bgcolor: '#10b981', width: 24, height: 24 }}>
                                      <CalendarTodayIcon sx={{ fontSize: 14 }} />
                                    </Avatar>
                                    <Typography variant="body2">
                                      {insp.date ? new Date(insp.date).toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: '2-digit', 
                                        year: 'numeric' 
                                      }) : '-'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                
                                {/* Inspector */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ bgcolor: '#f97316', width: 24, height: 24 }}>
                                      <PersonIcon sx={{ fontSize: 14 }} />
                                    </Avatar>
                                    <Typography variant="body2">
                                      {insp.inspector || 'Not specified'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                
                                {/* Notes */}
                                <TableCell>
                                  <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {insp.notes || 'No notes'}
                                  </Typography>
                                </TableCell>
                                
                                {/* Checklist */}
                                <TableCell>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ 
                                      bgcolor: '#0ea5e9', 
                                      color: 'white',
                                      borderColor: '#0ea5e9',
                                      '&:hover': { 
                                        bgcolor: '#0284c7',
                                        borderColor: '#0284c7'
                                      }
                                    }}
                                    startIcon={<FactCheckIcon sx={{ fontSize: 16 }} />}
                                  >
                                    {insp.checklist ? Object.keys(insp.checklist).length : 0} items
                                  </Button>
                                </TableCell>
                                
                                {/* Work Orders */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ bgcolor: '#ef4444', width: 24, height: 24 }}>
                                      <AssignmentIcon sx={{ fontSize: 14 }} />
                                    </Avatar>
                                    <Typography variant="body2">
                                      {insp.workOrders?.items?.length || 0} work orders
                                    </Typography>
                                  </Box>
                                </TableCell>
                                
                                {/* Actions */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title="Edit Inspection">
                                      <IconButton 
                                        size="small" 
                                        sx={{ color: '#10b981' }}
                                        onClick={() => navigate(`/fleet/inspections/${insp.id}`)}
                                      >
                                        <EditIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Inspection">
                                      <IconButton 
                                        size="small" 
                                        sx={{ color: '#ef4444' }}
                                        onClick={() => {
                                          if (window.confirm('Are you sure you want to delete this inspection?')) {
                                            // Add delete functionality here
                                            console.log('Delete inspection:', insp.id);
                                          }
                                        }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <FactCheckIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          No inspections found
                        </Typography>
                      </Box>
                                         )}
                   </CardContent>
                 </Card>
               </Grid>

               {/* Section Divider */}
               <Grid item xs={12}>
                 <Box sx={{ 
                   my: 3, 
                   borderBottom: '1px solid #e2e8f0',
                   position: 'relative',
                   '&::after': {
                     content: '""',
                     position: 'absolute',
                     bottom: '-6px',
                     left: '50%',
                     transform: 'translateX(-50%)',
                     width: '12px',
                     height: '12px',
                     backgroundColor: '#ff5722',
                     borderRadius: '50%',
                     border: '2px solid #ffffff',
                     boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                   }
                 }} />
               </Grid>

               {/* Warranty Records */}
                <Grid item xs={12} >
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ bgcolor: '#ff5722', width: 40, height: 40, mr: 2 }}>
                            <VerifiedUserIcon />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Warranty Records
                          </Typography>
                        </Box>
                        <Tooltip title="Add Warranty">
                          <IconButton
                            onClick={handleAddWarranty}
                            sx={{ 
                              bgcolor: '#ff5722', 
                              color: 'white',
                              '&:hover': { bgcolor: '#d84315' }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    
                    {currentVehicle.warrantyRecords?.items?.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ background: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Start</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>End</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentVehicle.warrantyRecords.items.map((wr, idx) => (
                              <TableRow key={wr.id} sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}>
                                <TableCell>{wr.company_name}</TableCell>
                                <TableCell>
                                  <Chip label={wr.warranty_type} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>{wr.startDate}</TableCell>
                                <TableCell>{wr.endDate}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <VerifiedUserIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          No warranty records found
                        </Typography>
                      </Box>
                                         )}
                   </CardContent>
                 </Card>
               </Grid>

               {/* Section Divider */}
               <Grid item xs={12}>
                 <Box sx={{ 
                   my: 3, 
                   borderBottom: '1px solid #e2e8f0',
                   position: 'relative',
                   '&::after': {
                     content: '""',
                     position: 'absolute',
                     bottom: '-6px',
                     left: '50%',
                     transform: 'translateX(-50%)',
                     width: '12px',
                     height: '12px',
                     backgroundColor: '#607d8b',
                     borderRadius: '50%',
                     border: '2px solid #ffffff',
                     boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                   }
                 }} />
               </Grid>

               {/* Registrations */}
                <Grid item xs={12} >
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ bgcolor: '#607d8b', width: 40, height: 40, mr: 2 }}>
                            <HowToRegIcon />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Registrations
                          </Typography>
                        </Box>
                        <Tooltip title="Add Registration">
                          <IconButton
                            onClick={handleAddRegistration}
                            sx={{ 
                              bgcolor: '#607d8b', 
                              color: 'white',
                              '&:hover': { bgcolor: '#455a64' }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    
                    {currentVehicle.registrations?.items?.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ background: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Registration #</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Annual Date</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentVehicle.registrations.items.map((reg, idx) => (
                              <TableRow key={reg.id} sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}>
                                <TableCell>{reg.registrationNumber}</TableCell>
                                <TableCell>{reg.annualDate}</TableCell>
                                <TableCell>{reg.expiryDate}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <HowToRegIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          No registrations found
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </>
      )}

      {/* Action Buttons - Bottom */}
      <Card sx={{ 
        mt: 4, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={handleCancel}
              variant="outlined"
              size="large"
              disabled={creating || updating}
              sx={{ 
                minWidth: 120,
                borderColor: '#e0e0e0',
                color: '#666',
                '&:hover': {
                  borderColor: '#999',
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={creating || updating}
              sx={{ 
                minWidth: 120,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
                }
              }}
            >
              {creating || updating ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                vehicle ? "Update Vehicle" : "Create Vehicle"
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Modal Components */}

      {/* Maintenance Modal */}
      <Dialog
        open={maintenanceModal}
        onClose={() => setMaintenanceModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Maintenance Record</DialogTitle>
        <DialogContent>
          <MaintenanceForm
            recordId={null}
            vehicleId={vehicle?.id}
            onClose={(success) => {
              setMaintenanceModal(false);
              if (success) {
                // Refresh vehicle data to show new maintenance record
                refetchVehicle();
              }
              // Don't call parent onClose when used as modal
            }}
            isEditing={true}
            isModal={true}
          />
        </DialogContent>
      </Dialog>

      {/* Assignment Modal */}
      <Dialog
        open={assignmentModal}
        onClose={() => setAssignmentModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Vehicle Assignment</DialogTitle>
        <DialogContent>
          <AssignmentModalForm
            vehicleId={vehicle?.id}
            vehicleName={`${vehicle?.make} ${vehicle?.model}`}
            onClose={() => setAssignmentModal(false)}
            onSuccess={() => {
              setAssignmentModal(false);
              // Refresh the vehicle data without page reload
              refetchVehicle();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Work Order Modal */}
      <Dialog
        open={workOrderModal}
        onClose={() => setWorkOrderModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Work Order</DialogTitle>
        <DialogContent>
          <WorkOrderForm
            vehicleId={vehicle?.id}
            onClose={(success) => {
              setWorkOrderModal(false);
              if (success) {
                // Refresh vehicle data to show new work order
                refetchVehicle();
              }
              // Don't call parent onClose when used as modal
            }}
            isEditing={true}
            isModal={true}
          />
        </DialogContent>
      </Dialog>

      {/* Inspection Modal */}
      <Dialog
        open={inspectionModal}
        onClose={() => setInspectionModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Inspection</DialogTitle>
        <DialogContent>
          <InspectionForm
            vehicleId={vehicle?.id}
            onClose={(success) => {
              setInspectionModal(false);
              if (success) {
                // Refresh vehicle data to show new inspection
                refetchVehicle();
              }
              // Don't call parent onClose when used as modal
            }}
            isEditing={true}
            isModal={true}
          />
        </DialogContent>
      </Dialog>

      {/* Warranty Modal */}
      <Dialog
        open={warrantyModal}
        onClose={() => setWarrantyModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Warranty Record</DialogTitle>
        <DialogContent>
          <WarrantyModalForm
            vehicleId={vehicle?.id}
            vehicleName={`${vehicle?.make} ${vehicle?.model}`}
            onClose={() => setWarrantyModal(false)}
            onSuccess={() => {
              setWarrantyModal(false);
              // Refresh the vehicle data without page reload
              refetchVehicle();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Registration Modal */}
      <Dialog
        open={registrationModal}
        onClose={() => setRegistrationModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Registration</DialogTitle>
        <DialogContent>
          <RegistrationModalForm
            vehicleId={vehicle?.id}
            vehicleName={`${vehicle?.make} ${vehicle?.model}`}
            onClose={() => setRegistrationModal(false)}
            onSuccess={() => {
              setRegistrationModal(false);
              // Refresh the vehicle data without page reload
              refetchVehicle();
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default VehicleForm;
